import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatDialogRef, MAT_DIALOG_DATA, MatSnackBar } from '@angular/material';
import { Chart } from 'chart.js';
import { GatewayService } from 'src/app/gateway.service';
import { LocalSaveService } from 'src/app/services/local-save-service.service';

interface MonitorPoint {
  ts: Date;
  rx: number | null;
  tx: number | null;
}

@Component({
  selector: 'app-monitor-realtime-ftth-dialog',
  templateUrl: './monitor-realtime-ftth-dialog.component.html',
  styleUrls: ['./monitor-realtime-ftth-dialog.component.scss']
})
export class MonitorRealtimeFtthDialogComponent implements OnInit, OnDestroy, AfterViewInit {

  @ViewChild('chartCanvas') chartCanvas: ElementRef<HTMLCanvasElement>;

  public sn: string;
  public idusuario: string;
  public tipoUsuario: number;
  public deviceLabel: string = '';
  public unit: string = 'dBm';
  public points: MonitorPoint[] = [];
  public isQuerying: boolean = false;
  public lastError: string = '';
  public intervalSec: number = 30;
  public batchSize: number = 10;
  public paused: boolean = false;
  public batchCount: number = 0;

  // Rangos permitidos (configurados en cert_ftth_optical_power) para dibujar
  // como lineas horizontales de referencia en el grafico.
  public txRangeMin: number | null = null;
  public txRangeMax: number | null = null;
  public rxRangeMin: number | null = null;
  public rxRangeMax: number | null = null;

  private chart: Chart | null = null;
  private timerId: any = null;
  private destroyed: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<MonitorRealtimeFtthDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private gateway: GatewayService,
    private http: HttpClient,
    private localSave: LocalSaveService,
    private snackBar: MatSnackBar
  ) {
    this.sn = (data && data.sn) || '';
    this.idusuario = (data && data.idusuario) || '';
    this.tipoUsuario = (data && data.tipoUsuario) || 1;
    this.deviceLabel = (data && data.deviceLabel) || this.sn;
    if (data && data.initialResult) {
      const p = this.extractPoint(data.initialResult);
      if (p) { this.points.push(p); }
      const par = data.initialResult.parametros || {};
      const rx = par.opticsrxpower || {};
      const tx = par.opticstxpower || {};
      this.rxRangeMin = (rx.rango_min != null) ? Number(rx.rango_min) : null;
      this.rxRangeMax = (rx.rango_max != null) ? Number(rx.rango_max) : null;
      this.txRangeMin = (tx.rango_min != null) ? Number(tx.rango_min) : null;
      this.txRangeMax = (tx.rango_max != null) ? Number(tx.rango_max) : null;
    }
  }

  ngOnInit(): void { }

  ngAfterViewInit(): void {
    setTimeout(() => this.initChart(), 0);
    this.loadConfigAndStart();
  }

  private loadConfigAndStart(): void {
    const url = this.gateway.globalip + '/api/PGV/CONFIG_CERT/MONITOR_CFG/LIST';
    const headers = { 'Authorization': 'p ' + this.localSave.get('OSR_TKN') };
    this.http.post(url, {}, { headers }).subscribe(
      (r: any) => {
        if (r && r.config) {
          if (Number(r.config.interval_sec) >= 1) this.intervalSec = Number(r.config.interval_sec);
          if (Number(r.config.batch_size)   >= 1) this.batchSize   = Number(r.config.batch_size);
        }
        this.startPolling();
      },
      () => {
        // Fallback a defaults si el endpoint falla
        this.startPolling();
      }
    );
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.stopTimer();
    if (this.chart) { this.chart.destroy(); this.chart = null; }
  }

  public close(): void { this.dialogRef.close(); }

  private startPolling(): void {
    this.paused = false;
    this.batchCount = 0;
    this.queryNow();
    this.stopTimer();
    this.timerId = setInterval(() => this.queryNow(), this.intervalSec * 1000);
  }

  private stopTimer(): void {
    if (this.timerId) { clearInterval(this.timerId); this.timerId = null; }
  }

  public resume(): void {
    if (!this.paused || this.destroyed) { return; }
    this.startPolling();
  }

  public queryNow(): void {
    if (this.isQuerying || this.destroyed || this.paused) { return; }
    this.isQuerying = true;
    this.lastError = '';
    const params = { sn: this.sn, idusuario: this.idusuario, tipoUsuario: this.tipoUsuario, skip_log: true };
    this.gateway.certificarFTTH(params).subscribe(
      (result: any) => {
        this.isQuerying = false;
        const p = this.extractPoint(result);
        if (p) {
          this.points.push(p);
          this.batchCount++;
          this.refreshChart();
          if (this.batchCount >= this.batchSize) {
            this.paused = true;
            this.stopTimer();
          }
        } else {
          this.lastError = 'Respuesta sin RX/TX';
        }
      },
      (err: any) => {
        this.isQuerying = false;
        this.lastError = 'Error en consulta';
      }
    );
  }

  private extractPoint(result: any): MonitorPoint | null {
    if (!result || !result.parametros) { return null; }
    const p = result.parametros;
    const rx = (p.opticsrxpower && p.opticsrxpower.valor_dBm != null) ? Number(p.opticsrxpower.valor_dBm) : null;
    const tx = (p.opticstxpower && p.opticstxpower.valor_dBm != null) ? Number(p.opticstxpower.valor_dBm) : null;
    if (rx == null && tx == null) { return null; }
    return { ts: new Date(), rx, tx };
  }

  public get latestRx(): number | null {
    for (let i = this.points.length - 1; i >= 0; i--) {
      if (this.points[i].rx != null) { return this.points[i].rx; }
    }
    return null;
  }

  public get latestTx(): number | null {
    for (let i = this.points.length - 1; i >= 0; i--) {
      if (this.points[i].tx != null) { return this.points[i].tx; }
    }
    return null;
  }

  public get rxMin(): number | null { return this.minOf(p => p.rx); }
  public get rxMax(): number | null { return this.maxOf(p => p.rx); }
  public get txMin(): number | null { return this.minOf(p => p.tx); }
  public get txMax(): number | null { return this.maxOf(p => p.tx); }
  public get rxAvg(): number | null { return this.avgOf(p => p.rx); }
  public get txAvg(): number | null { return this.avgOf(p => p.tx); }

  private minOf(sel: (p: MonitorPoint) => number | null): number | null {
    let m: number | null = null;
    for (const p of this.points) {
      const v = sel(p);
      if (v == null) { continue; }
      if (m == null || v < m) { m = v; }
    }
    return m;
  }
  private maxOf(sel: (p: MonitorPoint) => number | null): number | null {
    let m: number | null = null;
    for (const p of this.points) {
      const v = sel(p);
      if (v == null) { continue; }
      if (m == null || v > m) { m = v; }
    }
    return m;
  }
  private avgOf(sel: (p: MonitorPoint) => number | null): number | null {
    let sum = 0; let n = 0;
    for (const p of this.points) {
      const v = sel(p);
      if (v == null) { continue; }
      sum += v; n++;
    }
    if (n === 0) { return null; }
    return Math.round((sum / n) * 100) / 100;
  }

  public formatTs(d: Date): string {
    try {
      return d.toLocaleTimeString('es-PA', {
        timeZone: 'America/Panama',
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch (e) {
      const utc = d.getTime() + d.getTimezoneOffset() * 60000;
      const pa = new Date(utc - 5 * 3600000);
      const hh = ('0' + pa.getHours()).slice(-2);
      const mm = ('0' + pa.getMinutes()).slice(-2);
      const ss = ('0' + pa.getSeconds()).slice(-2);
      return hh + ':' + mm + ':' + ss;
    }
  }

  private initChart(): void {
    if (!this.chartCanvas || !this.chartCanvas.nativeElement) { return; }
    const ctx = this.chartCanvas.nativeElement.getContext('2d');
    if (!ctx) { return; }
    const n = Math.max(1, this.points.length);
    const constArr = (v: number | null) => v == null ? [] : new Array(n).fill(v);
    this.chart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: this.points.map(p => this.formatTs(p.ts)),
        datasets: [
          {
            label: 'ONT Optics TxPower',
            data: this.points.map(p => p.tx),
            borderColor: '#7e3ff2',
            backgroundColor: 'rgba(126,63,242,0.1)',
            fill: false,
            tension: 0.2,
            pointRadius: 3,
            spanGaps: true
          },
          {
            label: 'ONT Optics RxPower',
            data: this.points.map(p => p.rx),
            borderColor: '#2e7d32',
            backgroundColor: 'rgba(46,125,50,0.1)',
            fill: false,
            tension: 0.2,
            pointRadius: 3,
            spanGaps: true
          },
          {
            label: 'TX max',
            data: constArr(this.txRangeMax),
            borderColor: 'rgba(126,63,242,0.55)',
            backgroundColor: 'transparent',
            fill: false,
            borderWidth: 1,
            borderDash: [6, 4],
            pointRadius: 0,
            spanGaps: true
          },
          {
            label: 'TX min',
            data: constArr(this.txRangeMin),
            borderColor: 'rgba(126,63,242,0.55)',
            backgroundColor: 'transparent',
            fill: false,
            borderWidth: 1,
            borderDash: [6, 4],
            pointRadius: 0,
            spanGaps: true
          },
          {
            label: 'RX max',
            data: constArr(this.rxRangeMax),
            borderColor: 'rgba(46,125,50,0.55)',
            backgroundColor: 'transparent',
            fill: false,
            borderWidth: 1,
            borderDash: [6, 4],
            pointRadius: 0,
            spanGaps: true
          },
          {
            label: 'RX min',
            data: constArr(this.rxRangeMin),
            borderColor: 'rgba(46,125,50,0.55)',
            backgroundColor: 'transparent',
            fill: false,
            borderWidth: 1,
            borderDash: [6, 4],
            pointRadius: 0,
            spanGaps: true
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 250 },
        legend: {
          display: true,
          position: 'top',
          labels: {
            // Solo mostrar TX y RX originales; ocultar las 4 lineas de rango.
            filter: function (item: any) {
              return item.datasetIndex === 0 || item.datasetIndex === 1;
            }
          },
          onClick: function (e: any, legendItem: any) {
            // Replicamos el toggle por defecto pero arrastrando las 2 lineas de
            // rango asociadas (dataset 2/3 para TX, 4/5 para RX) para que se
            // oculten/muestren junto con su serie principal.
            var ci = (this as any).chart;
            var idx = legendItem.datasetIndex;
            var pairs: number[] = [idx];
            if (idx === 0) { pairs.push(2, 3); }
            else if (idx === 1) { pairs.push(4, 5); }
            for (var i = 0; i < pairs.length; i++) {
              var meta = ci.getDatasetMeta(pairs[i]);
              meta.hidden = (meta.hidden === null ? !ci.data.datasets[pairs[i]].hidden : null);
            }
            ci.update();
          }
        },
        scales: {
          yAxes: [{
            scaleLabel: { display: true, labelString: this.unit },
            ticks: { beginAtZero: false }
          }],
          xAxes: [{
            scaleLabel: { display: true, labelString: 'Tiempo' }
          }]
        },
        tooltips: {
          mode: 'index',
          intersect: false,
          // Excluir las 4 lineas de rango del tooltip (solo TX/RX reales).
          filter: function (item: any) {
            return item.datasetIndex === 0 || item.datasetIndex === 1;
          }
        }
      }
    });
  }

  private refreshChart(): void {
    if (!this.chart) { this.initChart(); return; }
    const n = Math.max(1, this.points.length);
    const constArr = (v: number | null) => v == null ? [] : new Array(n).fill(v);
    this.chart.data.labels = this.points.map(p => this.formatTs(p.ts));
    this.chart.data.datasets[0].data = this.points.map(p => p.tx);
    this.chart.data.datasets[1].data = this.points.map(p => p.rx);
    if (this.chart.data.datasets[2]) this.chart.data.datasets[2].data = constArr(this.txRangeMax);
    if (this.chart.data.datasets[3]) this.chart.data.datasets[3].data = constArr(this.txRangeMin);
    if (this.chart.data.datasets[4]) this.chart.data.datasets[4].data = constArr(this.rxRangeMax);
    if (this.chart.data.datasets[5]) this.chart.data.datasets[5].data = constArr(this.rxRangeMin);
    this.chart.update();
  }
}
